import { memoize } from 'lodash';
import { useRouter } from 'next/router';
import React, { ReactNode, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendArticleClickEvent } from 'src/helpers/analytics';

import { AuthPopup } from '@sravni/react-ugc/lib/AuthPopup';
import { useBoolean } from '@sravni/react-utils';
import { Tab } from '@sravni/ugc/lib/specialOffers/types';

import { SravniNewsSharedEnumsMaterialType, SravniNewsSharedEnumsPlacementType } from '../../../__generated__/news/Api';
import { TAKE_MATERIALS, TAKE_MATERIALS_INITIAL, TAKE_NEWS_INITIAL } from '../../../constants/news';
import { useGenerateTabsForSpecialOffers } from '../../../hooks/generateTabsForSpecialOffers';
import { useMaterialsList, useRubricType, useTagType } from '../../../hooks/news';
import { useOrganizationInfoFromQuery } from '../../../hooks/useOrganizationInfoFromQuery';
import { IMaterial, IRouterQuery } from '../../../interfaces/news';
import { fetchMoreItemsForMaterialsList } from '../../../reducers/news';
import { getCanLoadingMore, getMaterialsListLoading } from '../../../selectors/news';
import { signInUser, verifyUser } from '../../../services/auth/user';
import { checkIsExcludedRubric } from '../../../utils/news';
import { mainPageInfiniteScroll } from '../helpers/mainPageInfiniteScroll';

export interface ListContainerProps {
  loading: boolean;
  tabsForSpecialOffers: Tab[];
  list: IMaterial[];
  handleShowAuthPopup: () => void;
  triggerRef?: React.RefObject<HTMLDivElement>;
  createItemClickHandler?: (position: number, item: IMaterial) => () => void;
  children?: ReactNode;
}

const createItemClickHandler = memoize((position: number, item: IMaterial) => () => {
  sendArticleClickEvent(item.title, position);
});

export const withListContainer = () =>
  function <T>(Component: React.ComponentType<T & ListContainerProps>) {
    const WrappedComponent: React.FC<T> = (props) => {
      const dispatch = useDispatch();
      const list = useMaterialsList();
      const { query } = useRouter() as TRouter<IRouterQuery>;
      const tagType = useTagType();
      const organization = useOrganizationInfoFromQuery();
      let rubricType = useRubricType();
      const triggerRef = useRef();
      const removeHandlerRef = useRef<() => void>();
      const isExcludedRubric = checkIsExcludedRubric(rubricType);
      const isLoading = useSelector(getMaterialsListLoading);
      const canLoadingMore = useSelector(getCanLoadingMore);

      const [showAuthPopup, setShowAuthPopup] = useBoolean(false);

      const isNews =
        Boolean(query.organizationAlias) || ['novosti', 'novosti-partnerov', 'novosti-sporta'].includes(rubricType);
      const isPartnersNews = Boolean(query.organizationAlias) || rubricType === 'novosti-partnerov';

      const hasMoreMaterials = list.length >= (isNews ? TAKE_NEWS_INITIAL : TAKE_MATERIALS_INITIAL) && canLoadingMore;

      if (isPartnersNews) {
        rubricType = 'novosti-partnerov';
      }

      const tabsForSpecialOffers = useGenerateTabsForSpecialOffers({
        productTags: [query.tag],
        rubric: rubricType,
      });

      const loadMore = useCallback(async () => {
        if (hasMoreMaterials) {
          let type = SravniNewsSharedEnumsMaterialType.Article;
          if (isExcludedRubric) {
            type = SravniNewsSharedEnumsMaterialType.PrivateArticle;
          } else if (isNews) {
            type = SravniNewsSharedEnumsMaterialType.News;
          }

          await dispatch(
            fetchMoreItemsForMaterialsList({
              Alias: list[0].alias,
              Placement: isNews
                ? SravniNewsSharedEnumsPlacementType.NewsList
                : SravniNewsSharedEnumsPlacementType.ArticlesList,
              Take: TAKE_MATERIALS,
              Skip: 0,
              type,
              ...(rubricType && { Rubrics: [rubricType] }),
              ...(tagType && { Tags: [tagType] }),
              ...(organization && { organizationId: organization.id }),
            }),
          );
        }

        return;
      }, [dispatch, hasMoreMaterials, isExcludedRubric, isNews, list, organization, rubricType, tagType]);

      useEffect(() => {
        window.history.scrollRestoration = 'manual';

        return () => {
          window.history.scrollRestoration = 'auto';
        };
      }, []);

      useEffect(() => {
        if (removeHandlerRef.current) {
          removeHandlerRef.current();
        }
        const { remove } = mainPageInfiniteScroll(loadMore, triggerRef.current, isNews);
        removeHandlerRef.current = remove;
        return () => {
          remove();
        };
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
      }, [isNews, loadMore]);

      return (
        <>
          <Component
            {...{
              loading: isLoading,
              tabsForSpecialOffers,
              loadMore,
              list,
              handleShowAuthPopup: setShowAuthPopup.on,
              triggerRef,
              createItemClickHandler,
              ...props,
            }}
          />
          <AuthPopup
            showPopup={showAuthPopup}
            handleClose={setShowAuthPopup.off}
            signInUser={signInUser}
            verifyUser={verifyUser}
          />
        </>
      );
    };

    WrappedComponent.displayName = `withListContainer(${Component.displayName})`;

    return WrappedComponent;
  };
